/**
 * The remembered submitter (client, 2026-09-18).
 *
 * A manager uploads in batches, and retyping their own name and address on every upload is
 * the sort of friction that ends in `a@a.a`. The pair is kept in `localStorage` under
 * `sv.submitter` and seeded into every new wizard; the fields stay editable, and what the
 * reader last typed is what is remembered.
 *
 * It lives here rather than in `manager.ts` because that module is pure by contract — no
 * DOM, no network — and `localStorage` is neither. Every access is wrapped: a private
 * window, a blocked third-party context or a full quota must cost the reader a pre-filled
 * field, never the wizard.
 */
import type { MetadataForm, WizardState } from './manager';

export const SUBMITTER_STORAGE_KEY = 'sv.submitter';

export interface Submitter {
  name: string;
  email: string;
}

const EMPTY: Submitter = { name: '', email: '' };

/** What was stored, or two empty strings — never a throw and never a partial object. */
export function readSubmitter(): Submitter {
  try {
    const raw = window.localStorage.getItem(SUBMITTER_STORAGE_KEY);
    if (!raw) return EMPTY;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return EMPTY;
    const { name, email } = parsed as Partial<Submitter>;
    return {
      name: typeof name === 'string' ? name : '',
      email: typeof email === 'string' ? email : '',
    };
  } catch {
    return EMPTY;
  }
}

/** Remember the pair. A refusal to write is silent — it is a convenience, not a record. */
export function writeSubmitter(value: Submitter): void {
  try {
    window.localStorage.setItem(
      SUBMITTER_STORAGE_KEY,
      JSON.stringify({ name: value.name, email: value.email }),
    );
  } catch {
    /* an unwritable store is not a reason to fail an upload */
  }
}

/** A fresh wizard state with the remembered pair already in its form. */
export function seedSubmitter(state: WizardState, stored: Submitter = readSubmitter()): WizardState {
  if (stored.name === '' && stored.email === '') return state;
  const form: MetadataForm = {
    ...state.form,
    submitterName: stored.name,
    submitterEmail: stored.email,
  };
  return { ...state, form };
}
